/******************************************************************
 * ##: Location persistence decision
 * Decides whether an incoming automatic position should be stored, based on the
 * distance moved since the last stored position and the elapsed time. Pure function,
 * safe for unit testing. Action-triggered positions bypass this and are always stored.
 *
 * TL;DR: Store if moved >= 20m, or >= 60s since the last record.
 * History:
 * 23-07-2026: Created
 ******************************************************************/

import { LOCATION_DISTANCE_THRESHOLD_METERS, LOCATION_STATIONARY_INTERVAL_MS } from "constants/location";
import { haversineMeters } from "./haversine";

/**
 * Decide whether an incoming automatic position should be persisted.
 *
 * The stationary interval is measured against the last record's recorded_at (a
 * persisted timestamp), not an in-memory timer, so it keeps working after a reload.
 *
 * @param {object} params
 * @param {object|null} params.lastRecord - Last stored location record for the active service (or null)
 * @param {{ latitude: number, longitude: number }} params.candidate - Incoming coordinates
 * @param {number} params.now - Current time in ms (Date.now())
 * @param {number} [params.distanceThreshold] - Meters threshold (defaults to constant)
 * @param {number} [params.stationaryInterval] - Milliseconds threshold (defaults to constant)
 * @returns {boolean}
 */
export const shouldPersistPosition = ({
  lastRecord,
  candidate,
  now,
  distanceThreshold = LOCATION_DISTANCE_THRESHOLD_METERS,
  stationaryInterval = LOCATION_STATIONARY_INTERVAL_MS,
}) => {
  // First position for the service is always stored.
  if (!lastRecord) return true;

  const distance = haversineMeters({ latitude: lastRecord.latitude, longitude: lastRecord.longitude }, candidate);

  // Moved far enough: store.
  if (!Number.isNaN(distance) && distance >= distanceThreshold) {
    return true;
  }

  // Stationary or short movement: store if enough time passed since the last record.
  const lastTime = lastRecord.recorded_at ? new Date(lastRecord.recorded_at).getTime() : NaN;

  // If the last timestamp is unusable, do not silently drop the position.
  if (Number.isNaN(lastTime)) return true;

  return now - lastTime >= stationaryInterval;
};
